import {
  ANALYTICS_ACCESS,
  ARRANGEMENT_STATUSES_ISSUED,
  AUDIT_ACCESS,
  BULK_DRAFT_DISCARD,
  CAPABILITIES,
  ELEVATED_ACTING_NODE_MESSAGE,
  ELEVATED_OVER_ANCESTOR_MESSAGE,
  LOCATION_INTEL_ACCESS,
  POLICY_AUTHOR_REFUSAL,
  REVERT_RUNNING_ARRANGEMENT,
  SUPER_ADMIN_ADMINISTRATION,
  SUPER_ADMIN_PEER_REFUSAL,
  can,
  meetsRequirement,
  type AccessRole,
  type Capability,
  type CapabilityHolder,
  type CapabilityRequirement,
  type OrgUnitType,
  type PostAudience,
} from '@pams/shared';

/**
 * Capability checks in the UI, over the SAME table the API's authorisation middleware
 * reads (`@pams/shared`). One table, two consumers, no second definition to drift.
 *
 * ⚠️ NONE OF THIS IS ENFORCEMENT.
 *
 * It decides what to disable and what to hide. A NODAL_READ who opens devtools and
 * calls `POST /officers` gets a 403 from the API, and an out-of-scope read gets a 404
 * from the query itself — those are the controls. This is courtesy, and it is worth
 * saying out loud because a UI that looks like a permission system invites people to
 * treat it as one.
 */
export { can, CAPABILITIES };
export type { AccessRole, Capability };
/**
 * ⚠️ [C5b §5] AND ONE QUESTION ABOUT AN OFFICER RATHER THAN A ROLE — the same resolver the API's
 * `requireCapability` asks. `can` above stays role-only and every existing caller keeps it; a
 * route, page or nav row whose requirement names a FLAG capability — `ANALYTICS_ACCESS` and
 * [amended AUDIT-FLAG] `AUDIT_ACCESS`, the System section's two rows — goes through
 * `meetsRequirement` with the officer's `flags` from `/auth/me`, and nothing else reads those flags.
 * Still courtesy, not enforcement: the API refuses regardless.
 *
 * [LOCATION-INTEL] `LOCATION_INTEL_ACCESS` joins them: the Locational Intel row and route, the same value
 * its three API routes take.
 */
export { ANALYTICS_ACCESS, AUDIT_ACCESS, LOCATION_INTEL_ACCESS, SUPER_ADMIN_ADMINISTRATION, meetsRequirement };
export type { CapabilityHolder, CapabilityRequirement };

/** Convenience for the common "can they change anything at all?" question. */
export const isReadOnly = (role: AccessRole): boolean => !can(role, 'officer.write') && !can(role, 'org.write');

export const READ_ONLY_TOOLTIP = 'Your access is view-only.';

/**
 * ⚠️ [C1c §7] RAISING AN ARRANGEMENT IS A DIVISION'S WORK, AND THIS CANNOT BE A CAPABILITY.
 *
 * There is no "unit nodal" ROLE. `scopeForActor` branches on `SUPER_ADMIN` alone and never reads
 * `type`, and `Scope` carries no org unit type — so a `NODAL_ADMIN` at a police station and one at
 * a division hold IDENTICAL capabilities, and only the node they are posted at differs. `can()`
 * above therefore cannot answer this, and a capability minted for it would be granted to exactly
 * the roles that already hold `arrangement.write`.
 *
 * ⚠️ IT IS THE SAME PREDICATE THE SERVER USES (`assertMayAuthorArrangement`), so a door cannot be
 * open on a room that refuses. Before C1c a unit nodal reached a form whose source picker was
 * EMPTY — `SourceCell.tsx` filters to `type === 'DIVISION'` — and was told nothing at all.
 *
 * ⚠️ AND IT LIVES BESIDE `can` RATHER THAN IN A COMPONENT FILE. `react-refresh/only-export-components`
 * refuses a non-component export from one, and it is right: this is a rule, not a view.
 *
 * ─── ⚠️ [amended UX1 §8] IT TAKES THE DEPLOYMENT'S SETTING NOW, AND THAT IS A LIVE CAPABILITY
 *     CHANGE ────────────────────────────────────────────────────────────────────────────────
 *
 * `arrangement.nodalAuthor` DEFAULTS TO `false`, so a DIVISION nodal who could raise an order
 * before this pass cannot until a super admin turns it back on. That is the intent.
 *
 * ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, NOT A DIFFERENT
 * TEST — `mayArchiveTier`'s shape one screen down. `scopeForActor` maps SUPER_ADMIN to GLOBAL
 * and `settings.write` is `['SUPER_ADMIN']`, so the two sets are identical. It is a CAPABILITY
 * and never a role comparison (§10: authorisation is data-driven and there are no role
 * ladders), and the sentence it makes is honest: whoever throws this switch is not bound by
 * it, because they can throw it back in one click.
 *
 * ⚠️ THE SIGNATURE CHANGED RATHER THAN GAINING A DEFAULT, DELIBERATELY. A defaulted third
 * parameter would put `false` — or worse, `true` — in the omitted position at a call site
 * nobody revisited. Breaking every caller is the fail-loud direction, and it is what surfaced
 * the hand-written COPY of this predicate in `navigation.ts` that would otherwise have gone on
 * answering the old rule.
 */
/*
 * ─── ⚠️ [amended C2a §3] IT ANSWERS FOR **AUTHORING**, NOT ONLY FOR RAISING ──────────────
 *
 * The server guard it mirrors is `assertMayAuthorArrangement`, which now covers five verbs —
 * create, update, duplicate, publish and restamp. Fill-side verbs are deliberately outside it,
 * so a divisional nodal still names, swaps, unassigns, delegates and un-delegates.
 *
 * ⚠️ THE COMMISSIONERATE CLAUSE READS `type` WHERE THE SERVER READS **POSITION**, AND THAT
 * ASYMMETRY IS DELIBERATE. [C1e §5] MEASURED that `type === 'COMMISSIONERATE'` is
 * self-grantable — `type` is on `updateOrgUnitSchema` and `org.write` includes NODAL_ADMIN —
 * so the SERVER asks `ancestors.length === 0` instead (`isSeatedAtCommissionerate`). This
 * file CANNOT ask that: `orgUnitSummarySchema` carries `id`, `name`, `shortCode`, `type` and
 * `subType`, and no `parentId`, `depth` or `ancestors`.
 *
 * ⚠️ WHICH IS SAFE ONLY BECAUSE THIS IS AN OFFER GATE AND NEVER AUTHORISATION — [C1f §1c]'s
 * rule. The worst a forged `type` buys is a button that renders and then 403s. Adding
 * `ancestors` to the summary DTO to "fix" this would put the establishment's shape on every
 * `/auth/me`, which is the thing a subtree scope exists to withhold.
 */
export const mayAuthorArrangement = (
  role: AccessRole,
  orgUnitType: string | undefined,
  nodalsMayAuthor: boolean,
): boolean => {
  if (can(role, 'settings.write')) return true;
  /* The CP office authors regardless of the switch — the server's second short-circuit. */
  if (orgUnitType === 'COMMISSIONERATE') return true;
  if (orgUnitType === 'UNIT') return false;
  return nodalsMayAuthor;
};

/**
 * ⚠️ [C1f §1c] A UNIT IS NOT OFFERED THE ROUTING CONTROL, BECAUSE IT HAS NOTHING BENEATH IT.
 *
 * ⚠️ IT IS AN **OFFER** GATE, NOT AUTHORISATION, AND THE SERVER IS NOT NARROWED.
 * `delegableUnitsUnder` already answers `[]` for a leaf and `assertDelegationTargets` already
 * refuses a target outside the addressee, so a unit that legitimately splits WITHIN itself —
 * which the chain guard permits, one level only — is still free to. What this removes is a
 * control that would open onto an empty picker for the great majority of units, which is what
 * [C1c §7] found a unit nodal already meeting on the intake form.
 *
 * ⚠️ AND IT IS **NOT** AN ALIAS OF `mayAuthorArrangement`, WHICH HAS THE SAME BODY TODAY.
 * That one mirrors a SERVER refusal (`assertMayRaiseArrangement`) and narrows a verb the API
 * rejects; this one narrows a verb the API allows. Sharing them would mean that the day a
 * unit is permitted to raise an order, this control silently appears too — two facts, one
 * literal, and the forgotten half fails in silence ([WINGS.1]'s rule).
 *
 * ⚠️ §10's escalation rule does not bite. `type` IS writable by a `NODAL_ADMIN` on their own
 * node (C1e MEASURED it), but nothing downstream relies on this answer: relabelling buys a
 * caller only a control the server would have honoured anyway. C1e's rule is about crossing a
 * SUPER_ADMIN boundary, and this crosses none.
 */
export const mayDelegateAllocation = (orgUnitType: string | undefined): boolean => orgUnitType !== 'UNIT';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [LIST-FILTERS] WHO IS OFFERED "ADJUST FILTERS" ON THE ORDER LISTS — ANYONE WHOSE SCOPE HOLDS
 *  A DIVISION TO CHOOSE.
 *
 *  The filter narrows Manage Arrangements and Durga Puja PA to the divisions an order asks force
 *  of, and its picker is the caller's SCOPED org tree. A unit's scoped tree holds no division at
 *  all — `scopeForActor` roots everyone but a SUPER_ADMIN at their own node, and a division sits
 *  ABOVE a unit — so offering the button there opens onto an empty picker, which is what [C1c §7]
 *  found a unit nodal meeting on the intake form. `mayDelegateAllocation`'s reasoning one function
 *  up, for a different verb.
 *
 *  ⚠️ THE FIRST CLAUSE IS THE SERVER'S `GLOBAL` SCOPE, AS A CAPABILITY — `settings.write` is
 *  `['SUPER_ADMIN']` and `scopeForActor` maps exactly that role to `GLOBAL`, so a super admin
 *  posted at a police station still sees every division. A capability rather than a role
 *  comparison keeps this file's no-role-ladder property (§10).
 *
 *  ⚠️ `org.read` IS ASKED BECAUSE THE PICKER READS THE ORG TREE, and it is held by every dashboard
 *  role, so it narrows nothing a list page can render for; it is what keeps `NONE` from being
 *  offered a picker over an endpoint it would be refused.
 *
 *  ⚠️ IT READS THE ACTING UNIT (`actingOrgUnitTypeOf`), so a unit nodal elevated to act at its
 *  division is offered the filter exactly as one seated there is ([C5c §11g]) — that scope does
 *  hold the division. An unknown type is offered, `mayDelegateAllocation`'s reading: the worst it
 *  buys is a picker that says there are no divisions in scope.
 *
 *  ⚠️ AN OFFER GATE ONLY. The server accepts `divisionOrgUnitId` from any caller and intersects it
 *  with their scope; a unit caller who sent one would merely narrow a list to what they already
 *  see. And the page also shows the button whenever a filter IS applied, so whatever this answers,
 *  a stored filter can always be cleared.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayFilterByDivision = (
  person:
    | {
        readonly accessRole: AccessRole;
        readonly orgUnit?: { type: OrgUnitType } | null;
        readonly actingOrgUnit?: { type: OrgUnitType } | null;
      }
    | null
    | undefined,
): boolean =>
  person
    ? can(person.accessRole, 'org.read') &&
      (can(person.accessRole, 'settings.write') || actingOrgUnitTypeOf(person) !== 'UNIT')
    : false;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §2] WHO MAY EDIT AN ORDER THAT HAS BEEN ISSUED — the twin of
 *  `assertMayEditIssuedArrangement`, and the FIRST predicate in this file that reads a fact
 *  about a RECORD rather than about the caller.
 *
 *  ⚠️ WHICH IS WHY IT CANNOT BE A CAPABILITY, IN EITHER DIRECTION. No key can express "raised
 *  this one"; and a `arrangement.editIssued` would be held by exactly the roles that hold
 *  `arrangement.write`, which §10's own rule says grants nothing.
 *
 *  ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, EXPRESSED AS A
 *  CAPABILITY — `mayCompleteShortfall`'s line one screen down, verbatim, so this file keeps its
 *  no-role-ladder property (§10). `scopeForActor` maps `SUPER_ADMIN` to `GLOBAL` and
 *  `settings.write` is `['SUPER_ADMIN']`, so the two sets are identical.
 *
 *  ⚠️ THE ORDER'S OWN STATE IS THE CALLER'S TO PASS, NOT THIS FUNCTION'S TO FETCH. A DRAFT is
 *  outside the rule entirely, and `ARRANGEMENT_STATUSES_ISSUED` is the tuple that says so —
 *  never `=== 'PUBLISHED'`, which [D4.4] MEASURED as unreachable once the first officer is named.
 *
 *  ⚠️ AND AN UNKNOWN CREATOR ANSWERS `true`. This is an OFFER gate: the server holds the record
 *  and refuses whatever this returns, so failing closed here would hide the edit door from the
 *  order's own author on a payload that had not arrived.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayEditIssuedArrangement = (
  role: AccessRole,
  officerId: string | undefined,
  record: { status: string; createdByOfficerId: string } | undefined,
): boolean => {
  if (can(role, 'settings.write')) return true;
  if (!record || officerId === undefined) return true;
  if (!ARRANGEMENT_STATUSES_ISSUED.includes(record.status as (typeof ARRANGEMENT_STATUSES_ISSUED)[number])) {
    return true;
  }
  return record.createdByOfficerId === officerId;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3c.2] WHO IS OFFERED `Complete With Short-Fall` — A CAPABILITY **AND** A SEAT.
 *
 *  A division asked for ten U/A men that can supply seven closes its share at seven with a
 *  written reason. The client named three audiences for the control: a super admin, a nodal
 *  admin "only for level 1, i.e. division admin", and a nodal monitor.
 *
 *  ⚠️ THE ROLE HALF IS A REAL CAPABILITY, MINTED FOR IT. `{SUPER_ADMIN, NODAL_ADMIN,
 *  NODAL_MONITOR}` is not any pre-existing grant list and no conjunction of them produces it —
 *  every key a monitor holds is co-extensive with `NODAL_READ`'s, whom the client did not name.
 *  See `fulfilment.completeShortfall`'s declaration for what minting it costs.
 *
 *  ⚠️ THE SEAT HALF CANNOT BE A CAPABILITY, WHICH IS `mayAuthorArrangement`'s ARGUMENT ONE
 *  SCREEN UP. There is no "unit nodal" ROLE — `scopeForActor` branches on `SUPER_ADMIN` alone
 *  and `Scope` carries no org unit type — so a `NODAL_ADMIN` at a police station and one at a
 *  division hold identical capabilities and differ only in the node they are posted at.
 *
 *  ⚠️ AND IT IS AN **OFFER** GATE, NOT AUTHORISATION — `mayDelegateAllocation`'s posture, not
 *  `mayAuthorArrangement`'s. The server deliberately does NOT enforce the seat: doing so would
 *  REMOVE an act a station-seated nodal admin holds today through `arrangement.write`, which is
 *  a narrowing nobody asked for. What this buys is that the button is not offered where it
 *  would be the wrong desk's decision; the worst a forged `type` gets is a control the API
 *  would have honoured anyway.
 *
 *  ⚠️ "LEVEL 1" IS READ AS `!== 'UNIT'` RATHER THAN `=== 'DIVISION'`, AND THE READING IS STATED
 *  BECAUSE IT IS A CHOICE. Read literally, `=== 'DIVISION'` would also refuse a nodal admin
 *  seated at the COMMISSIONERATE — level ZERO, above every division — which is the CP office
 *  being denied an act it can take for all ten of them. `!== 'UNIT'` is the reading that
 *  excludes what the instruction meant to exclude (a station speaking for its division's
 *  supply) and nothing else, and it is the same predicate `mayDelegateAllocation` already uses.
 *
 *  ⚠️ THE SEAT RULE BINDS THE NODAL **ADMIN** ALONE, because the instruction qualified that
 *  role alone. It is expressed as `arrangement.write` rather than as `role === 'NODAL_MONITOR'`
 *  so that this file keeps its no-role-ladder property (§10) — and among the three holders the
 *  writers ARE exactly the roles the parenthetical was about.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayCompleteShortfall = (role: AccessRole, orgUnitType: string | undefined): boolean => {
  if (!can(role, 'fulfilment.completeShortfall')) return false;
  /* The server's `scope.kind === 'GLOBAL'` short-circuit — `settings.write` is SUPER_ADMIN. */
  if (can(role, 'settings.write')) return true;
  /* A monitor carries no seat rule; the instruction qualified the nodal ADMIN alone. */
  if (!can(role, 'arrangement.write')) return true;
  return orgUnitType !== 'UNIT';
};

/**
 * ⚠️ [UX1 §3c] WHOSE QUESTION IS THIS ROW ANSWERING — "have I done my part", or "is this duty
 * covered"?
 *
 * [C1f §1g] settled that a delegated-but-unnamed post stays AMBER. The client has since asked
 * for GREEN, and §3 reconciles the two by being precise about the READER rather than by
 * choosing between them. `postFillStateOf` takes the answer; this decides it.
 *
 * ⚠️ IT IS NOT AN ALIAS OF `mayDelegateAllocation`, AND UNLIKE THAT PAIR IT DOES NOT EVEN
 * SHARE A BODY. That one is `!== 'UNIT'` and this is `=== 'DIVISION'`, so they disagree at a
 * COMMISSIONERATE — which is the case that matters, because the CP office may delegate and
 * must never read a handed-down slot as staffed.
 *
 * ⚠️ THE `SUPER_ADMIN` CLAUSE IS LOAD-BEARING AND IS NOT DECORATION. `scopeForActor` maps that
 * role to `GLOBAL`, so a super admin posted at a division org unit sees EVERY division's
 * lines — and without this they would read another division's fully delegated post as green.
 * It is a role comparison rather than a capability deliberately: the fact being asked is "is
 * this reader globally scoped", and minting a capability for it would imply an authorisation
 * decision that does not exist (§10 bans a role `switch` in AUTHORISATION, and this decides a
 * colour).
 *
 * ⚠️ AND IT FAILS TO `oversight`. An unknown or missing org unit type gets the reading that
 * cannot overstate, which is the same direction `DEFAULT_UI_MODE` falls in and for the same
 * reason: the safe answer is the one everybody already agrees on.
 */
export const fillAudienceFor = (role: AccessRole, orgUnitType: string | undefined): PostAudience =>
  orgUnitType === 'DIVISION' && role !== 'SUPER_ADMIN' ? 'addressee' : 'oversight';

/**
 * ⚠️ [C1e §5] WHO MAY ARCHIVE, GIVEN WHICH OF THE THREE ACTS IT WOULD BE.
 *
 * The twin of `assertMayArchive`, and it exists for `mayAuthorArrangement`'s reason one screen
 * up: the narrowing is a NODE, which no capability can carry, so `can()` alone cannot answer it.
 *
 * ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, NOT A DIFFERENT
 * TEST. `scopeForActor` maps `SUPER_ADMIN` to `GLOBAL` and `lifecycle.archivePublished` is
 * `['SUPER_ADMIN']`, so the two sets are identical — which is also why a super admin posted at a
 * police station is not caught by the node clause below.
 *
 * ⚠️ AND IT IS COURTESY, AS EVERYTHING IN THIS FILE IS. The API refuses with a 403 whatever the
 * button does; what this buys is that the operator is told BEFORE the confirmation rather than
 * after it, which is possible only because `confirmArchive` already fetches the impact first.
 */
export const mayArchiveTier = (role: AccessRole, orgUnitType: string | undefined, tier: string): boolean => {
  if (can(role, 'lifecycle.archivePublished')) return true;
  return tier === 'FILING' && orgUnitType === 'COMMISSIONERATE' && can(role, 'arrangement.delete');
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §3] WHO IS OFFERED A POLICY'S AUTHORING VERBS — A CAPABILITY **AND** A SEAT.
 *
 *  The twin of the server's `assertMayAuthorPolicy`. `policy.write` and `policy.publish` now list
 *  NODAL_ADMIN, and the client's pairing is `(accessRole, orgUnitType)`: a nodal admin at the
 *  COMMISSIONERATE authors, and one at a division holds the identical role and does not (§3d). No
 *  capability can carry a node, so `can()` answers the role half and this adds the seat —
 *  `mayAuthorArrangement`'s argument one screen up.
 *
 *  ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, expressed as a
 *  capability — `settings.write` is `['SUPER_ADMIN']` and `scopeForActor` maps that role to
 *  `GLOBAL`, so the two sets are identical, and this file keeps its no-role-ladder property (§10).
 *  A super admin posted below the root is therefore still an author, exactly as the server says.
 *
 *  ⚠️ THE SEAT READS `type` WHERE THE SERVER READS **POSITION** — the asymmetry `mayAuthorArrangement`
 *  records, for its reason: `/auth/me` carries no `ancestors`, and adding them would publish the
 *  establishment's shape on every session. Safe ONLY because this is an OFFER gate; a divisional
 *  nodal who relabels their own node is shown a door the server then refuses, and
 *  `policyAuthoring.test.ts` CLAIM 3b is what proves the refusal.
 *
 *  ⚠️ TWO FUNCTIONS OVER ONE SEAT, NOT ONE. Publish is a separate key on the server for the
 *  `place.merge` argument, and a later pass may narrow one half without the other — so a screen
 *  asks the question it means. There is deliberately no `mayConfigurePolicy`: it would be
 *  `can(role, 'policy.configure')` verbatim, an alias carrying no information.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const seatedToAuthorPolicies = (role: AccessRole, orgUnitType: string | undefined): boolean =>
  can(role, 'settings.write') || orgUnitType === 'COMMISSIONERATE';

export const mayAuthorPolicy = (role: AccessRole, orgUnitType: string | undefined): boolean =>
  can(role, 'policy.write') && seatedToAuthorPolicies(role, orgUnitType);

export const mayPublishPolicy = (role: AccessRole, orgUnitType: string | undefined): boolean =>
  can(role, 'policy.publish') && seatedToAuthorPolicies(role, orgUnitType);

/**
 * ⚠️ [C5c §3] THE REASON A DISABLED POLICY CONTROL GIVES — AND THERE ARE TWO, BECAUSE THE REFUSAL IS
 * TWO FACTS. A nodal admin below the root HOLDS the capability and lacks the SEAT, so "your access is
 * view-only" would tell them something false about themselves ([C3c.2]'s `shortfallOnly` lesson);
 * they are told who writes policies instead, in the server's own sentence from `@pams/shared`. A
 * reader or a monitor lacks the role, and keeps the app-wide sentence.
 */
export const policyRefusal = (role: AccessRole): string =>
  can(role, 'policy.write') ? POLICY_AUTHOR_REFUSAL : READ_ONLY_TOOLTIP;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §11] THE LEVEL A PERSON ACTS AT — `actingOrgUnit` WHEN THEIR POSTING IS ELEVATED, ELSE
 *  THE POSTING. Every offer gate and every role label that pairs `(accessRole, orgUnitType)` reads
 *  this and never `orgUnit.type` directly, so an elevated division admin is offered what an HQ admin
 *  is offered and is labelled `HQ Admin` (11g) — one expression, never a type read at each call site.
 *
 *  Still courtesy: the API decides reach from its own per-request resolution of the same setting,
 *  and `actingOrgUnit` is absent from the payload for every unelevated officer.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const actingOrgUnitTypeOf = (
  person:
    | {
        orgUnit?: { type: OrgUnitType } | null;
        actingOrgUnit?: { type: OrgUnitType } | null;
      }
    | null
    | undefined,
): OrgUnitType | undefined => (person?.actingOrgUnit ?? person?.orgUnit)?.type;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §11c] AN ELEVATED ADMIN SEES THE ANCESTOR'S OWN MEMBERS AND NEVER CHANGES THEM — the
 *  OFFER gate for `assertReachAllowsWriteAt`'s first rule and `assertNotActingNode`, reading the same
 *  two `@pams/shared` sentences so the tooltip and the 403 cannot disagree.
 *
 *  `elevatedRefusalAt` answers for an officer POSTED at `orgUnitId` (edit, transfer from or to, create into);
 *  `elevatedNodeRefusal` for the acting NODE itself (edit, deactivate). `undefined` means "offer it".
 *  `actingOrgUnit` is on the caller's identity only while a nodal role is elevated, so for everyone
 *  else both answer `undefined` with no further test.
 *
 *  ⚠️ STILL COURTESY, AND NARROWER THAN THE SERVER. The second server rule — a nodal posting whose
 *  unit acts ABOVE the caller's own level — needs the posting's chain, which no list row carries;
 *  it stays a 403 after submit, naming `ELEVATED_TARGET_ABOVE_MESSAGE`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
type ElevatedActor = { actingOrgUnit?: { id: string } | null } | null | undefined;

export const elevatedRefusalAt = (actor: ElevatedActor, orgUnitId: string | undefined): string | undefined =>
  actor?.actingOrgUnit && orgUnitId === actor.actingOrgUnit.id ? ELEVATED_OVER_ANCESTOR_MESSAGE : undefined;

export const elevatedNodeRefusal = (actor: ElevatedActor, orgUnitId: string | undefined): string | undefined =>
  actor?.actingOrgUnit && orgUnitId === actor.actingOrgUnit.id ? ELEVATED_ACTING_NODE_MESSAGE : undefined;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7A §7] MAY THIS VIEWER CHANGE THAT ROW — WHEN THE ROW IS ANOTHER SUPER ADMIN.
 *
 *  The OFFER gate for `officer.service.ts`' `assertMayAdminister`: editing (phone included),
 *  transferring, deactivating or changing the role of a super admin who is not the viewer needs the
 *  viewer to meet `SUPER_ADMIN_ADMINISTRATION` — a SUPER_ADMIN holding `master_superadmin`. Returns the
 *  server's own sentence (`@pams/shared`) so the tooltip and the 403 cannot disagree, or `undefined`
 *  meaning "offer it". A NODAL_ADMIN viewer is covered too: the only role above theirs is SUPER_ADMIN,
 *  and the role half of the requirement is SUPER_ADMIN alone.
 *
 *  ⚠️ NARROWER THAN THE SERVER, DELIBERATELY. The server also refuses a holder writing to ANOTHER
 *  holder, and this cannot say so: `OfficerDto` carries no flags, because putting holder status on every
 *  `officer.read` row would tell NODAL_READ and NODAL_MONITOR which accounts to attack. A holder editing
 *  a holder therefore gets the 403 after submit, naming `MASTER_SUPER_ADMIN_REFUSAL` — the drawer and the
 *  transfer modal already show the server's message ([C5c §11]'s `ELEVATED_TARGET_ABOVE_MESSAGE` shape).
 *
 *  ⚠️ IT ASKS `meetsRequirement`, NEVER THE FLAGS — this file is already a resolver caller, so F7's
 *  census of `officerFlags.test.ts` stays at six files and no flag literal appears here.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const superAdminPeerRefusal = (
  viewer: (CapabilityHolder & { readonly id: string }) | null | undefined,
  row: { readonly id: string; readonly accessRole: AccessRole } | null | undefined,
): string | undefined =>
  viewer &&
  row &&
  row.accessRole === 'SUPER_ADMIN' &&
  row.id !== viewer.id &&
  !meetsRequirement(viewer, SUPER_ADMIN_ADMINISTRATION)
    ? SUPER_ADMIN_PEER_REFUSAL
    : undefined;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §4] WHO MAY USE THE TEMPLATE PAGES — `arrangement.write` **AND** A COMMISSIONERATE SEAT.
 *
 *  The twin of the server's `assertMayRunTemplates`: a SUPER_ADMIN, or a NODAL_ADMIN acting at the
 *  commissionerate root. A divisional nodal admin holds the identical role and is refused, because the
 *  set is commissionerate-wide — 182 orders addressed to every division at once — which is
 *  `mayAuthorPolicy`'s seat argument, one screen over.
 *
 *  ⚠️ IT TAKES THE OFFICER, NOT A TYPE, so no caller can hand it the POSTING's type where the ACTING
 *  one belongs: `actingOrgUnitTypeOf` is read here, once ([C5c §11g]). An elevated division admin
 *  acting at the root is "HQ in every respect" and is offered the pages; the same admin unelevated
 *  is not.
 *
 *  ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, as a capability —
 *  `settings.write` is `['SUPER_ADMIN']` — so this file keeps its no-role-ladder property (§10).
 *
 *  ⚠️ AND IT IS AN OFFER GATE: the seat reads `type` where the server reads POSITION, which is safe
 *  for `mayAuthorPolicy`'s stated reason — the worst a relabelled node buys is a page whose every
 *  request then 403s.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayUseTemplates = (
  person:
    | {
        readonly accessRole: AccessRole;
        readonly orgUnit?: { type: OrgUnitType } | null;
        readonly actingOrgUnit?: { type: OrgUnitType } | null;
      }
    | null
    | undefined,
): boolean => (person ? mayUseTemplatesAt(person.accessRole, actingOrgUnitTypeOf(person)) : false);

/**
 * ⚠️ [P7B.1 §4] THE SAME RULE, OVER A ROLE AND AN ACTING TYPE — for the sidebar, whose `visibleSections`
 * holds exactly those two and never the officer. [amended] `mayUseTemplates` above "takes the officer,
 * not a type, so no caller can hand it the posting's type": that still holds for every screen, which
 * calls it; this twin exists for the one caller that is handed the type ALREADY RESOLVED —
 * `AppShell` and the ⌘K palette both pass `actingOrgUnitTypeOf(officer)` — and `mayUseTemplates`
 * delegates to it, so the rule is written once.
 */
export const mayUseTemplatesAt = (role: AccessRole, actingOrgUnitType: string | undefined): boolean =>
  can(role, 'arrangement.write') && (can(role, 'settings.write') || actingOrgUnitType === 'COMMISSIONERATE');

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §6] THE TWO `master_superadmin` OVERRIDES — OFFER GATES OVER THE SAME REQUIREMENTS THE
 *  API ROUTES TAKE (`BULK_DRAFT_DISCARD`, `REVERT_RUNNING_ARRANGEMENT`), so a hidden control and a
 *  refused request cannot disagree.
 *
 *  ⚠️ `meetsRequirement`, NEVER THE FLAGS. This file is one of the six resolver callers
 *  `officerFlags.test.ts` F7 pins; every page asks one of these two functions and no page reads an
 *  officer's flags itself. A super admin WITHOUT the flag is refused (the role half alone is not
 *  enough), and a NODAL_ADMIN given the flag is refused on the role half — a flag only adds to what
 *  the role already allows.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayBulkDiscardDrafts = (holder: CapabilityHolder | null | undefined): boolean =>
  holder ? meetsRequirement(holder, BULK_DRAFT_DISCARD) : false;

export const mayRevertRunning = (holder: CapabilityHolder | null | undefined): boolean =>
  holder ? meetsRequirement(holder, REVERT_RUNNING_ARRANGEMENT) : false;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7C §2] WHO IS OFFERED DUTY ANALYTICS — A DASHBOARD ROLE ACTING AT THE COMMISSIONERATE OR A
 *  DIVISION, OR A SUPER ADMIN AT ANY SEAT.
 *
 *  The server decides by POSITION: `raiserLevelOf` on the ACTING unit, a GLOBAL scope read as the
 *  commissionerate, anything else a 403 naming `DUTY_ANALYTICS_LEVEL_REFUSAL`. This reads the TYPE
 *  label, which is `mayAuthorPolicy`'s safe asymmetry one screen over: an offer gate. The worst a
 *  relabelled node, or a DIVISION-typed unit at depth two, buys is a page whose one request 403s —
 *  and the page prints the server's sentence when it does.
 *
 *  ⚠️ NO CAPABILITY IS MINTED. `dashboard.access` answers "may they open the product's read
 *  screens", and the narrowing is a NODE, which no capability can express ([C1c §7]'s argument).
 *
 *  ⚠️ THE FIRST CLAUSE IS THE SERVER'S `scope.kind === 'GLOBAL'` SHORT-CIRCUIT, as a capability —
 *  `settings.write` is `['SUPER_ADMIN']` — so this file keeps its no-role-ladder property (§10).
 *
 *  ⚠️ `can`, NEVER `meetsRequirement`: no flag is involved, so `officerFlags.test.ts` F7's resolver
 *  census does not move. And the officer twin reads `actingOrgUnitTypeOf`, so an elevated unit nodal
 *  acting at a division is offered the page exactly as one seated there is ([C5c §11g]).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const mayReadDutyAnalyticsAt = (role: AccessRole, actingOrgUnitType: string | undefined): boolean =>
  can(role, 'dashboard.access') &&
  (can(role, 'settings.write') || actingOrgUnitType === 'COMMISSIONERATE' || actingOrgUnitType === 'DIVISION');

export const mayReadDutyAnalytics = (
  person:
    | {
        readonly accessRole: AccessRole;
        readonly orgUnit?: { type: OrgUnitType } | null;
        readonly actingOrgUnit?: { type: OrgUnitType } | null;
      }
    | null
    | undefined,
): boolean => (person ? mayReadDutyAnalyticsAt(person.accessRole, actingOrgUnitTypeOf(person)) : false);
